import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useMobile } from 'common-nextjs';
import { useState } from 'react';
import ModalTitle from '~/components/Modal/ModalTitle';
import FilterModalBottomButton from '~/components/Search/FilterModal/FilterModalBottomButton';
import UniqueFilteringModal from '~/components/Search/FilterModal/UniqueFilteringModal';
import ExpandableFilteringList from '~/components/Search/FilteringList/ExpandableFilteringList';
import PillContainer from '~/components/Search/PillContainer';
import SearchSort from '~/components/Search/SearchSort';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import FilterIcon from '~/public/static/svg/FilterIcon';
import { RailsFacetGroup } from '~/typings/services/rails/facets';

export default function PinnedKeyDetails() {
  const mobile = useMobile();
  const { facets, filterState, filterCount } = useFacetFiltering();

  const keyFacets = facets
    .filter(
      facet =>
        facet.slug !== 'category' && facet.slug !== 'sale' && !!facet.children,
    )
    .slice(0, 2);

  return (
    <>
      {!mobile && filterCount > 0 && (
        <>
          <MobileAdditionalFiltersPopover />
          <div className="h-8 self-center border-r" />
        </>
      )}

      {keyFacets.map(facetGroup => (
        <PinnedKeyDetail key={facetGroup.slug} facetGroup={facetGroup} />
      ))}

      <SearchSort />

      {mobile && !!filterState.similar_to && <MobileAdditionalFiltersPopover />}
    </>
  );
}

function PinnedKeyDetail({ facetGroup }: { facetGroup: RailsFacetGroup }) {
  const { filterState } = useFacetFiltering();
  const selectedCount = filterState[facetGroup.slug]?.length || 0;
  const mobile = useMobile();

  const [open, setOpen] = useState(false);

  function onRequestClose() {
    setOpen(false);
  }

  if (mobile) {
    return (
      <div className="flex-shrink-0">
        <button
          className={clsx(
            'flex h-10 items-center gap-2 whitespace-nowrap rounded-full border bg-white px-4 text-sm',
            {
              'border-primary-500': selectedCount > 0,
            },
          )}
          onClick={() => setOpen(true)}
        >
          <span className="font-semibold">{facetGroup.name}</span>
          {selectedCount > 0 && (
            <span className="text-gray-500">({selectedCount})</span>
          )}
          <ChevronDownIcon className="h-4 w-4" />
        </button>

        <UniqueFilteringModal
          onRequestClose={onRequestClose}
          open={open}
          height={facetGroup.children?.length! > 10 ? 'tall' : 'short'}
        >
          <QuickFilterModalTitle
            onRequestClose={onRequestClose}
            facetGroup={facetGroup}
          />

          <div className="flex-1 px-4">
            <ExpandableFilteringList facet={facetGroup} maxHeight={false} />
          </div>

          <FilterModalBottomButton onClick={onRequestClose} />
        </UniqueFilteringModal>
      </div>
    );
  } else {
    return (
      <>
        <Popover className="relative">
          <PopoverButton
            className={clsx(
              'flex h-10 items-center gap-2 rounded-full border bg-white px-4 text-sm',
              {
                'border-primary-500': selectedCount > 0,
              },
            )}
          >
            <span className="font-semibold">{facetGroup.name}</span>
            {selectedCount > 0 && (
              <span className="text-gray-500">({selectedCount})</span>
            )}
            <ChevronDownIcon className="h-4 w-4" />
          </PopoverButton>

          <PopoverPanel className="shadow-raised absolute left-0 top-full z-40 mt-2 max-h-80 w-64 overflow-y-auto rounded border bg-white p-4">
            <div className="mb-4 font-semibold">{facetGroup.name}</div>
            <ExpandableFilteringList facet={facetGroup} maxHeight={false} />
          </PopoverPanel>
        </Popover>
      </>
    );
  }
}

function MobileAdditionalFiltersPopover() {
  const mobile = useMobile();
  const { filterCount } = useFacetFiltering();

  const [open, setOpen] = useState(false);

  function onRequestClose() {
    setOpen(false);
  }

  if (mobile) {
    return (
      <div className="flex-shrink-0">
        <button
          className="flex h-10 items-center gap-2 whitespace-nowrap rounded-full border bg-white px-4 text-sm"
          onClick={() => setOpen(true)}
        >
          <span className="font-semibold">Additional Filters</span>
          <ChevronDownIcon className="h-4 w-4" />
        </button>

        <UniqueFilteringModal
          onRequestClose={onRequestClose}
          open={open}
          height="short"
        >
          <ModalTitle className="sticky top-0 z-10 flex rounded-t bg-white px-4 py-3">
            <div className="flex-1 text-left"></div>
            <div className="flex-1 whitespace-nowrap">Additional Filters</div>
            <div className="flex-1 text-right"></div>
          </ModalTitle>

          <div className="flex-1 px-4">
            <PillContainer />
          </div>

          <FilterModalBottomButton onClick={onRequestClose} />
        </UniqueFilteringModal>
      </div>
    );
  } else {
    return (
      <Popover className="relative">
        <PopoverButton className="flex h-10 items-center gap-2 rounded-full border bg-white px-4 text-sm">
          <FilterIcon fill="currentColor" />
          <span className="font-semibold">Filters</span>
          <span className="text-gray-500">({filterCount})</span>
        </PopoverButton>

        <PopoverPanel className="shadow-raised absolute left-0 top-full z-40 mt-2 max-h-80 max-w-80 overflow-y-auto rounded border bg-white p-4">
          <div className="mb-4 font-semibold">Filters</div>
          <PillContainer />
        </PopoverPanel>
      </Popover>
    );
  }
}

function QuickFilterModalTitle({
  facetGroup,
  onRequestClose,
}: {
  facetGroup: RailsFacetGroup;
  onRequestClose: () => void;
}) {
  const { changeFacetSelection } = useFacetFiltering();

  function clearAll() {
    changeFacetSelection(facetGroup.slug, null);
    onRequestClose();
  }

  return (
    <ModalTitle className="sticky top-0 z-10 flex rounded-t bg-white px-4 py-3">
      <div className="flex-1 text-left">
        <button
          className="text-primary-500 pr-4 text-sm font-semibold"
          onClick={clearAll}
        >
          Clear
        </button>
      </div>
      <div className="flex-1 whitespace-nowrap">{facetGroup.name}</div>
      <div className="flex-1 text-right"></div>
    </ModalTitle>
  );
}
